/**
 * Work list state machine.
 *
 * Owns the filter/sort/search/page state and exposes setters that
 * trigger a re-render callback. Extracted from work.ts to stay under
 * the max-line-count structural rule — also makes the controller
 * testable without dragging the whole render pipeline in.
 *
 * Call `initWorkState(triggerRerender)` from the view module once, then
 * wire the exported `setWork*` functions to action-registry handlers.
 */

import type { WorkStats } from '../../api/work.js'
import type { WorkListFilters } from './work_filters.js'

type Trigger = () => void

const _state: WorkListFilters = {
  type: null,
  status: null,
  priority: null,
  assignee: null,
  tags: [],
  search: '',
  orderBy: 'updated_at',
  order: 'DESC',
  page: 1,
}

let _stats: WorkStats | undefined
let _assignees: string[] = []
let _trigger: Trigger = () => {}
let _searchDebounceId: ReturnType<typeof setTimeout> | null = null

export function initWorkState(trigger: Trigger): void {
  _trigger = trigger
}

export function getWorkFilters(): WorkListFilters {
  return _state
}

export function getWorkStats(): WorkStats | undefined {
  return _stats
}

export function setWorkStatsSnapshot(s: WorkStats): void {
  _stats = s
}

export function getWorkAssignees(): string[] {
  return _assignees
}

export function setWorkAssigneesSnapshot(a: string[]): void {
  _assignees = a
}

export function hasActiveFilters(): boolean {
  return (
    _state.type !== null ||
    _state.status !== null ||
    _state.priority !== null ||
    _state.assignee !== null ||
    _state.tags.length > 0 ||
    _state.search.trim() !== ''
  )
}

// ── Setters — each resets page to 1 and re-renders ──

export function setWorkTypeFilter(type: string | null): void {
  _state.type = isWorkType(type) ? type : null
  _state.page = 1
  _trigger()
}

export function setWorkStatusFilter(status: string | null): void {
  _state.status = isWorkStatus(status) ? status : null
  _state.page = 1
  _trigger()
}

export function setWorkPriorityFilter(priority: string | null): void {
  _state.priority = isWorkPriority(priority) ? priority : null
  _state.page = 1
  _trigger()
}

export function setWorkAssignee(assignee: string | null): void {
  // Free-text assignee name, or the '@me' sentinel — both pass through as-is;
  // 'null' clears. No client-side validation: the value is either a name from
  // the /principals list or the server-resolved sentinel.
  _state.assignee = assignee && assignee.length > 0 ? assignee : null
  _state.page = 1
  _trigger()
}

export function toggleWorkTag(tag: string): void {
  if (!tag) {
    return
  }
  const i = _state.tags.indexOf(tag)
  _state.tags = i >= 0 ? _state.tags.filter((t) => t !== tag) : [..._state.tags, tag]
  _state.page = 1
  _trigger()
}

export function setWorkSort(spec: string): void {
  // data-arg is "orderBy:order" e.g. "updated_at:DESC"
  const [ob, dir] = spec.split(':')
  if (ob === 'updated_at' || ob === 'created_at') {
    _state.orderBy = ob
  }
  if (dir === 'ASC' || dir === 'DESC') {
    _state.order = dir
  }
  _state.page = 1
  _trigger()
}

export function setWorkSearchInput(value: string): void {
  // Debounce ~200ms — matches Galaxy's --t-med so the feel is
  // responsive without hammering the API on every keystroke.
  _state.search = value
  _state.page = 1
  if (_searchDebounceId) {
    clearTimeout(_searchDebounceId)
  }
  _searchDebounceId = setTimeout(() => {
    _trigger()
  }, 220)
}

export function clearWorkFilters(): void {
  _state.type = null
  _state.status = null
  _state.priority = null
  _state.assignee = null
  _state.tags = []
  _state.search = ''
  _state.page = 1
  _trigger()
}

export function setWorkPage(page: number): void {
  _state.page = Math.max(1, page)
  _trigger()
}

// ── Type guards ──

function isWorkType(v: string | null): v is 'epic' | 'story' | 'task' | 'bug' {
  return v === 'epic' || v === 'story' || v === 'task' || v === 'bug'
}
function isWorkStatus(
  v: string | null,
): v is 'open' | 'in_progress' | 'done' | 'cancelled' | 'ready' {
  return v === 'open' || v === 'in_progress' || v === 'done' || v === 'cancelled' || v === 'ready'
}
function isWorkPriority(v: string | null): v is 'critical' | 'high' | 'medium' | 'low' {
  return v === 'critical' || v === 'high' || v === 'medium' || v === 'low'
}
